import axios, { AxiosRequestConfig, Method } from "axios";
import { t } from "i18next";
import { APIRequestOptions, QueryParams } from "../../interface/api";
import { resetUserState } from "../../redux/reducers/userSlice";
import { persistor, store } from "../../redux/store";
import { AppConfig } from "../config/config";
import { ApiError } from "./ApiError";

let queryString: string = "";

const Api = async <TRequest, TResponse>({
  method,
  endpoint,
  headers = {},
  body,
  filter,
  signal,
  customToken,
  customLanguage,
}: APIRequestOptions<TRequest>): Promise<TResponse> => {
  function toQueryString(filterVal: QueryParams[]): string {
    if (filterVal.length === 0) return "";
    // Create a query string
    const queryStrings = filterVal.map((obj) =>
      Object.keys(obj)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(String(obj[key]))}`,
        )
        .join("&"),
    );
    // Final query strings with '&' and '?'
    return `?${queryStrings.join("&")}`;
  }

  if (filter !== undefined) {
    queryString = toQueryString(filter);
  } else {
    queryString = "";
  }

  const lang =
    customLanguage?.slice(0, 2) ??
    (localStorage.getItem("i18nextLng")?.slice(0, 2) || "en");
  const token = customToken ?? (localStorage.getItem("token") || null);
  const isFormData = body instanceof FormData;

  const finalUrl =
    queryString !== ""
      ? `${AppConfig.BASE_API_URL}${endpoint}${queryString}`
      : `${AppConfig.BASE_API_URL}${endpoint}`;

  const config: AxiosRequestConfig = {
    url: finalUrl,
    method: method as Method,
    headers: {
      "Accept-Language": lang,
      Authorization: token ? `${token}` : "",
      ...headers,
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
    },
    data: method !== "GET" ? body : undefined,
    signal,
    timeout: 60000,
  };

  try {
    const response = await axios(config);

    const data = response.data;

    if (data.Status === false) {
      // Check for 401
      if (data.Code === 401) { 
        localStorage.clear(); 
        store.dispatch(resetUserState());
        await persistor.purge();
        throw new ApiError(
          data.ErrorMessage ||
            data.errorMessage ||
            "Session expired. Please login again.",
          response.status,
          401,
        );
      }
    } 
    if (data.Status === false) {
      throw new ApiError(
        data.ErrorMessage || data.errorMessage || t("error_occurred"),
        response.status,
        data.Code || response.status,
      );
    }

    return data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Extract message and code from response
        const message =
          error.response.data?.errorMessage ||
          error.response.data?.ErrorMessage ||
          t("error_occurred");
        const status = error.response.status;
        const code = error.response.data?.Code || status;
        if (code === 401) {
          // Dispatch Redux action to reset user state
          localStorage.clear();
          store.dispatch(resetUserState());
        }
        throw new ApiError(message, status, code);
      } else if (error.request) {
        // Request was made but no response received
        throw new ApiError("No response received from the server", 0, 0);
      } else {
        // Something else happened in setting up the request
        throw new ApiError(`Request Setup Error: ${error.message}`, 0, 0);
      }
    } else if (error instanceof ApiError) {
      // Re-throw ApiError
      throw error;
    } else {
      // Handle unexpected errors
      throw new ApiError("Unexpected error occurred", 0, 0);
    }
  }
};

export default Api;
